import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TestsHierarchicalList from "./TestsHierarchicalList";
import TestDetails from "./TestDetails";

const TestCasesContent = ({
    selectedProject,
    testCases,
    setTestCasesToggler,
    loading = false,
}) => {
    const [activeItem, setActiveItem] = useState(null);
    const [leftPanelWidth, setLeftPanelWidth] = useState(420);
    const isResizing = useRef(false);

    const MIN_WIDTH = 380;
    const MAX_WIDTH = 500;

    const navigate = useNavigate();

    const handleOpenProjectSettings = () => {
        if (selectedProject?.id) {
            navigate(`/project/${selectedProject.id}`);
        }
    };

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isResizing.current) return;
        const container = document.querySelector(".testCasesMainContent");
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const newWidth = e.clientX - containerRect.left;
        setLeftPanelWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth)));
    }, []);

    const handleMouseUp = useCallback(() => {
        if (!isResizing.current) return;
        isResizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }, []);

    useEffect(() => {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    return (
        <Box className="testCasesContentContainer">
            <Box className="testCasesTopSection">
                <Typography className="testCasesTitle">
                    Test Cases
                </Typography>
                <Box className="testCasesActions">
                    <Box className="testCasesActionsSettings">
                        <IconButton
                            className="testCasesActionsSettingsIconButton"
                            onClick={handleOpenProjectSettings}
                        >
                            <SettingsOutlinedIcon color="secondary" />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <Box className="testCasesMainContentContainer" sx={{ position: "relative" }}>
                {testCases && testCases?.length ? (
                    <Box className="testCasesMainContent">
                        <TestsHierarchicalList
                            projectId={selectedProject?.id}
                            testCases={testCases}
                            activeItem={activeItem}
                            setActiveItem={setActiveItem}
                            setTestCasesToggler={setTestCasesToggler}
                            panelWidth={leftPanelWidth}
                        />
                        <Box
                            className="resizeHandle"
                            onMouseDown={handleMouseDown}
                        />
                        <TestDetails
                            activeItem={activeItem}
                            setActiveItem={setActiveItem}
                            testCases={testCases}
                            setTestCasesToggler={setTestCasesToggler}
                        />
                    </Box>
                ) : (
                    <Box className="testCasesEmpty">
                        <Typography className="testCasesEmptyLabel" color="text.secondary">
                            Loading Test Cases...
                        </Typography>
                    </Box>
                )}

                {loading ? (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.65)",
                            zIndex: 50,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            pointerEvents: "auto",
                        }}
                        role="progressbar"
                        aria-busy="true"
                    >
                        <CircularProgress color="secondary" />
                        <Typography color="text.secondary">
                            Loading test cases...
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

export default TestCasesContent;
